import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { colors } from '../../../../../../colorVars';
import { useWebsocketStore } from '../../../../../../stores/websocketStore/websocketProvider';
import { buildMarketDataKeys } from '../../../../../../stores/websocketStore/utils/marketDataRouting';
import {
  // isBeforeNineThirty,
  isBeforeStartTime,
  validateTimeStamp,
} from '../../timestampFilter/timeStampFilter';
import Candle from './Candle';
import { candleNumberProcessor } from './utils/candleNumberProcessor';
import { checkIfNewCandle } from './utils/checkIfNewCandle';
import { createMarketEventCandleTimeStamp } from './utils/createMarketEventCandleTimeStamp';
import { generateInitialTimeStamp } from './utils/generateInitialNextTimeStamp';
import {
  buildLiveCandleUpdate,
  candlesAreEqual,
} from './utils/buildLiveCandleUpdate';
import { getMostRecentMarketEvent } from './utils/getMostRecentMarketEvent';
import { resolveLiveCandleForChart } from './utils/resolveLiveCandleForChart';
import {
  resolveChartLiveCandle,
  resolveMarketEventTimeStampForChart,
  shouldPreferMarketEventCandle,
} from './utils/resolveChartLiveCandle';

const MIN_VISIBLE_BODY_HEIGHT = 2;

function buildVisibleBody(scaleY, open, close) {
  const top = scaleY(Math.max(open, close));
  const bottom = scaleY(Math.min(open, close));
  const rawHeight = bottom - top;
  if (rawHeight >= MIN_VISIBLE_BODY_HEIGHT) {
    return {
      rectHeight: rawHeight,
      rectYPos: top,
    };
  }
  return {
    rectHeight: MIN_VISIBLE_BODY_HEIGHT,
    rectYPos: top - MIN_VISIBLE_BODY_HEIGHT / 2,
  };
}

export interface CandleShape {
  timeStamp: string;
}
interface MarketEventCandidate {
  timeStamp: number | null;
  price: number | null;
}
interface Ref {
  current: any;
}
interface ZoomState {
  k: any
}
interface Props {
  currentCandle: CandleShape;
  caliber: number;
  scaleX: (value: number) => number;
  scaleY:(value: number) => number;
  symbol: string;
  hash: string;
  brokerage?: string;
  loading:boolean;
  currentZoomState: ZoomState;
  portalRef: Ref;
  size:number;
  sessionWindows: Array<any>;
  secondToLast: CandleShape;
  numberOfCandles: number;
  updateOldCandles:(value: any) => void;
  millisBetweenCandles: number;
  priceMinimum: number;
  priceMaximum: number;
  setPriceMinimum:(value: number) => void;
  setPriceMaximum: (value: number) => void;
  dataSource: string;
  showCandleNumbers: boolean;
  startHour: string | number;
  startMin: string | number;
  timeUnit: string;
  candleDuration: number;
}
export const CurrentCandle: React.FC<Props> = observer(
  ({
    currentCandle,
    caliber,
    scaleX,
    scaleY,
    symbol,
    hash,
    brokerage,
    loading,
    currentZoomState,
    portalRef,
    size,
    sessionWindows,
    secondToLast,
    numberOfCandles,
    updateOldCandles,
    millisBetweenCandles,
    priceMinimum,
    priceMaximum,
    setPriceMinimum,
    setPriceMaximum,
    dataSource,
    showCandleNumbers,
    startHour,
    startMin,
    timeUnit,
    candleDuration,
  }) => {
    const [processedCandle, setProcessedCandle] = useState(null);
    const [unprocessedCandle, setUnprocessedCandle] = useState(null);
    const nextCandleTimeStampRef = useRef(null);
    const unprocessedCandleRef = useRef(null);
    const priceMinimumRef = useRef(priceMinimum);
    const priceMaximumRef = useRef(priceMaximum);
    const { priceDataStore, chartCandleStore } = useWebsocketStore();
    unprocessedCandleRef.current = unprocessedCandle;
    priceMinimumRef.current = priceMinimum;
    priceMaximumRef.current = priceMaximum;
    const marketDataHash =
      buildMarketDataKeys({
        symbol,
        dataSource,
        brokerage,
      }).find(
        (key) =>
          priceDataStore.trades[key] ||
          priceDataStore.timeSales[key] ||
          priceDataStore.lastQuote[key]
      ) ?? hash;
    const trade = priceDataStore.trades[marketDataHash] ?? null;
    const lastTrade = priceDataStore.lastTrade[marketDataHash] ?? null;
    const lastQuote = priceDataStore.lastQuote[marketDataHash] ?? null;
    const liveCandle =
      resolveLiveCandleForChart({
        candles: priceDataStore.candles,
        symbol,
        dataSource,
        brokerage,
        hash: marketDataHash,
        timeUnit,
        candleDuration,
      }) ?? null;
    const mostRecentMarketEvent: MarketEventCandidate | null =
      getMostRecentMarketEvent({
        trade,
        lastTrade,
        lastQuote,
      });
    const price = mostRecentMarketEvent?.price ?? null;
    const currentTimeStamp = mostRecentMarketEvent?.timeStamp ?? null;
    const resolvedChartLiveCandle = resolveChartLiveCandle({
      liveCandle,
      currentMarketEventTimeStamp: currentTimeStamp,
      currentChartCandleTimeStamp: unprocessedCandle?.timeStamp,
      millisBetweenCandles,
    });
    const chartLiveCandle = shouldPreferMarketEventCandle({
      timeUnit,
      marketEventPrice: price,
    })
      ? null
      : resolvedChartLiveCandle;

    // PRD Functional Requirement #6: ChartCandleStore is authoritative for routes it has
    // seeded or updated. resolveChartLiveCandle and the rest of this component's local advance
    // logic below are only a pre-store fallback. Once the store has any history for this route,
    // the `currentCandle` prop (driven by RefactoredChartContainer's store reaction) is the only
    // source of truth. A per-bucket guard is not enough for 60m futures charts: an empty
    // sessionWindows fallback can locally bucket a live 1m candle onto a neighboring grid and
    // render it over the store-owned series.
    const storeOwnsCurrentRoute = Boolean(
      chartCandleStore?.getHistory({
        symbol,
        dataSource,
        brokerage,
        timeUnit,
        candleDuration,
        limit: 1,
      })?.length
    );
    const storeOwnsCurrentBucket = Boolean(
      unprocessedCandle &&
        chartCandleStore?.hasCandle({
          symbol,
          dataSource,
          brokerage,
          timeUnit,
          candleDuration,
          timeStamp: unprocessedCandle.timeStamp,
        })
    );

    // USE EFFECT RUNS WHEN COMPONENT RECEIVES A NEW CURRENT CANDLE FROM PARENT
    useEffect(() => {
      if (currentCandle && millisBetweenCandles) {
        setUnprocessedCandle(currentCandle);
        const [isBefore, startTimeStamp] = isBeforeStartTime(
          dataSource,
          startHour,
          startMin,
          currentCandle.timeStamp,
          timeUnit
        );
        if (isBefore) {
          nextCandleTimeStampRef.current = startTimeStamp;
        } else {
          const newTimeStamp = generateInitialTimeStamp({
            timeUnit,
            candleDuration,
            millisBetweenCandles,
            currentCandle,
          });
          nextCandleTimeStampRef.current = newTimeStamp;
        }
      }
    }, [currentCandle]);

    useEffect(() => {
      if (storeOwnsCurrentRoute || storeOwnsCurrentBucket) {
        // ChartCandleStore already owns this route; the store-driven render reaction in
        // RefactoredChartContainer is authoritative for it. Don't fight it with a second,
        // locally-advanced version of the same candle.
        return;
      }
      if (!chartLiveCandle || !millisBetweenCandles || loading) {
        return;
      }

      const liveUpdate = buildLiveCandleUpdate({
        liveCandle: chartLiveCandle,
        currentCandle: unprocessedCandleRef.current,
        millisBetweenCandles,
        numberOfCandles,
        timeUnit,
        candleDuration,
        sessionWindows,
        priceMinimum: priceMinimumRef.current,
        priceMaximum: priceMaximumRef.current,
      });
      if (!liveUpdate) {
        return;
      }

      const {
        type,
        closedCandle,
        nextCandle,
        nextBounds,
        nextCandleTimeStamp,
      } = liveUpdate;

      nextCandleTimeStampRef.current = nextCandleTimeStamp;

      if (nextBounds.priceMinimum !== priceMinimumRef.current) {
        priceMinimumRef.current = nextBounds.priceMinimum;
        setPriceMinimum(nextBounds.priceMinimum);
      }
      if (nextBounds.priceMaximum !== priceMaximumRef.current) {
        priceMaximumRef.current = nextBounds.priceMaximum;
        setPriceMaximum(nextBounds.priceMaximum);
      }

      if (type === 'advance' && closedCandle) {
        updateOldCandles({
          closedCandle,
          nextCandle,
        });
      }

      if (
        type !== 'noop' &&
        !candlesAreEqual(unprocessedCandleRef.current, nextCandle)
      ) {
        unprocessedCandleRef.current = nextCandle;
        setUnprocessedCandle(nextCandle);
      }
    }, [
      chartLiveCandle?.timeStamp,
      chartLiveCandle?.open,
      chartLiveCandle?.high,
      chartLiveCandle?.low,
      chartLiveCandle?.close,
      chartLiveCandle?.volume,
      millisBetweenCandles,
      loading,
      numberOfCandles,
      timeUnit,
      candleDuration,
      sessionWindows,
      storeOwnsCurrentRoute,
      storeOwnsCurrentBucket,
    ]);

    // potential fixes for candle skipping. Move lines 60+61 into check Fn. Replace lastCandleTimeStamp state with a ref so change is immediate.
    //RUNS EACH TIME PRICE CHANGES
    useEffect(() => {
      if (storeOwnsCurrentRoute || storeOwnsCurrentBucket) {
        // Same rationale as the effect above: once the store owns this route, this raw
        // trade/quote-tick fallback path must not advance it locally either.
        return;
      }
      if (chartLiveCandle) {
        return;
      }
      if (price !== null && !loading && millisBetweenCandles && unprocessedCandle) {
        const currentPrice = Number(price);
        const currentCandleTimeStamp = resolveMarketEventTimeStampForChart({
          marketEventTimeStamp: currentTimeStamp,
          nextCandleTimeStamp: nextCandleTimeStampRef.current,
        });
        if (validateTimeStamp(dataSource, currentCandleTimeStamp)) {
          if (
            checkIfNewCandle({
              currentCandleTimeStamp,
              nextCandleTimeStampRef,
            })
          ) {
            const newTimeStamp = createMarketEventCandleTimeStamp({
              nextCandleTimeStampRef,
              currentCandleTimeStamp,
              millisBetweenCandles,
              timeUnit,
            });
            const slotAdvance = Math.max(
              Math.round(
                (Number(newTimeStamp) - Number(unprocessedCandle.timeStamp)) /
                  millisBetweenCandles
              ),
              1
            );
            const nextCandle = {
              ...unprocessedCandle,
              high: currentPrice,
              low: currentPrice,
              close: currentPrice,
              open: currentPrice,
              slotIndex:
                (unprocessedCandle.slotIndex ?? numberOfCandles - 1) +
                slotAdvance,
              timeStamp: newTimeStamp,
            };
            updateOldCandles({
              closedCandle: unprocessedCandle,
              nextCandle,
            });
            unprocessedCandleRef.current = nextCandle;
            setUnprocessedCandle(() => ({
              ...nextCandle,
            }));
          } else {
            if (currentPrice < priceMinimum) setPriceMinimum(currentPrice);
            if (currentPrice > priceMaximum) setPriceMaximum(currentPrice);
            const candleToUpdate = { ...unprocessedCandle };
            candleToUpdate.close = currentPrice;
            if (currentPrice > unprocessedCandle?.high)
              candleToUpdate.high = currentPrice;
            if (currentPrice < unprocessedCandle?.low)
              candleToUpdate.low = currentPrice;
            unprocessedCandleRef.current = candleToUpdate;
            setUnprocessedCandle(candleToUpdate);
          }
        }
      }
    }, [
      chartLiveCandle,
      price,
      currentTimeStamp,
      loading,
      millisBetweenCandles,
      dataSource,
      numberOfCandles,
      timeUnit,
      storeOwnsCurrentRoute,
      storeOwnsCurrentBucket,
    ]);

    //USE EFFECT RUNS WHEN UNPROCESSED CANDLE CHANGES OR ANY OF THE SCALES CHANGE
    useEffect(() => {
      if (
        unprocessedCandle !== null &&
        scaleY !== null &&
        scaleX !== null &&
        caliber !== null
      ) {
        const { rectHeight, rectYPos } = buildVisibleBody(
          scaleY,
          unprocessedCandle.open,
          unprocessedCandle.close
        );
        const candle = {
          zoomMult: currentZoomState === null ? 1 : currentZoomState.k,
          candleNumber: candleNumberProcessor(secondToLast, unprocessedCandle),
          rectWidth: caliber,
          rectHeight,
          rectYPos,
          rectXPos: scaleX(unprocessedCandle.slotIndex ?? numberOfCandles - 1),
          fill:
            unprocessedCandle.open < unprocessedCandle.close
              ? colors.green
              : colors.red,
          lineHeight:
            scaleY(unprocessedCandle.low) - scaleY(unprocessedCandle.high),
          lineYPos: scaleY(unprocessedCandle.high),
          lineXPos:
            scaleX(unprocessedCandle.slotIndex ?? numberOfCandles - 1) + caliber / 2,
          timeStamp: unprocessedCandle.timeStamp,
          candleOpen: unprocessedCandle.open,
          candleClose: unprocessedCandle.close,
          candleHigh: unprocessedCandle.high,
          candleLow: unprocessedCandle.low,
        };
        setProcessedCandle(candle);
      }
    }, [unprocessedCandle, caliber, scaleX, scaleY]);

    return (
      <>
        {processedCandle ? (
          <Candle
            candle={processedCandle}
            portalRef={portalRef}
            size={size}
            showCandleNumbers={showCandleNumbers}
            timeUnit={timeUnit}
          />
        ) : null}
      </>
    );
  }
);
